// Dialog "Préparer ma simulation" — flow rapide depuis un débrief Sales Coach Réel.
// Pré-rempli depuis l'axe d'amélioration cliqué, l'utilisateur ajuste offre/persona
// et lance le simulateur en un clic.

import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Mic, Loader2, AlertCircle, Package, UserCircle, Target, Sparkles, ShieldAlert, Building2 } from "lucide-react";
import { accountApi, authApi } from "../lib/api";
import { useQuota, classifyQuota } from "../hooks/useQuota";
import { MultiSelect } from "./ui/multi-select";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Input } from "./ui/input";
import { useTranslation } from "react-i18next";
import { localizeCompanySize, localizeIndustry } from "../lib/target-options-i18n";
import { pickPersonaField } from "../lib/persona-i18n";
import { localizePersonaOption } from "../lib/persona-options-i18n";
import { pickDecisionMaker } from "./PersonaUtils";

// Aligné sur la logique SimulationCaseWizard (Valérie, commit d0197ad) :
// industry est multi-select avec un marqueur "Autre" qui ouvre un champ libre,
// taille d'entreprise en single-select. Liste dupliquée d'AccountPage L976/L1007 —
// si modifiée là-bas, synchroniser ici (TODO : extraire dans un module partagé).
const CUSTOM_INDUSTRY = "__autre_industry__";
const COMPANY_SIZE_OPTIONS = [
  "Micro-entreprises",
  "TPE/TPI",
  "PME/PMI",
  "ETI",
  "Grandes entreprises",
  "Secteur public",
];
const INDUSTRY_OPTIONS = [
  "Administration - fonction publique",
  "Agroalimentaire",
  "Artisanat d'art",
  "Associations",
  "Banques – assurances - services financiers",
  "Chimie - plastique - conditionnement",
  "Commerce de détail - grande distribution",
  "Communication - marketing - information",
  "Construction – bâtiment - travaux publics",
  "Culture – sports – loisirs",
  "Energie",
  "Enseignement - formation",
  "Environnement – récupération – tri – recyclage - traitement des déchets – matériaux - de l'eau",
  "Équipement - matériel pour activités professionnelles",
  "Fabrication - commerce de gros d'articles destinés à la vente",
  "Gestion - administration des entreprises",
  "Hôtellerie - restauration - tourisme",
  "Immobilier",
  "Industrie textile",
  "Informatique",
  "Ingénieurs d'études et de recherche - chercheurs",
  "Logistique - transports",
  "Matériel électrique – électronique - optique",
  "Mécanique, métallurgie",
  "Minerais – minéraux - sidérurgie",
  "Professions juridiques",
  "Santé, action sociale",
  "Services aux particuliers – collectivités - entreprises",
];

export type CoachTrainingContext = {
  simulatorKey: string;          // ex: "DISCOVERY"
  simulatorLabel: string;        // ex: "Découverte (Discovery)"
  axeTitle?: string;             // titre de l'axe d'amélioration cliqué
  citation?: string;             // citation du transcript
  suggestedReformulation?: string; // ce qu'il aurait fallu dire
  contextNotes?: string;         // notes contextuelles du débrief original
  scoreGlobal?: number;          // pour calculer une difficulté suggérée
  prospectName?: string | null;
  prospectCompany?: string | null;
  // Chantier 2.2 — Session débrief d'origine, pour création d'un cas privé persistant
  realCallSessionId?: string;
};

interface Props {
  open: boolean;
  context: CoachTrainingContext | null;
  onClose: () => void;
  onLaunch: (payload: {
    simulatorKey: string;
    offerId: string;
    personaId: string;
    focus: string;
    difficulty: "easy" | "medium" | "hard";
    // Aligné Valérie (commit d0197ad) : obligatoires en B2B Sales uniquement,
    // optionnels sinon. industry est un array (marker CUSTOM_INDUSTRY filtré
    // côté backend + remplacé par customIndustry si "Autre" sélectionné).
    industry?: string[];
    companySize?: string;
    customIndustry?: string;
    // 2026-06-12 — Contexte spécifique au simulateur (table `contexts`).
    // Optionnel : null si "Aucun contexte" choisi. Stocké sur target.contextId
    // côté backend, propagé dans le payload N8N via les variables contextName/
    // Description/PlayerBrief/AgentInstructions (cf training.ts L678+).
    contextId?: string | null;
  }) => Promise<void> | void;
}

type Offer = { id: string; name: string; description?: string };
type ContextOption = {
  id: string;
  simulatorId: string;
  nameFr: string;
  nameEn: string;
  descriptionFr?: string | null;
  descriptionEn?: string | null;
  isActive: boolean;
};
// Un persona BizCoach est un profil type, pas une personne nommée : il est
// identifié par sa `function` (cf. src/db/schema.ts — pas de firstName/lastName).
// `painPoints` / `decisionMakerProfile` servent à distinguer deux personae
// de même fonction dans la liste déroulante.
type Persona = {
  id: string;
  function?: string;
  painPoints?: string | null;
  decisionMakerProfile?: string | null;
  // targetId renseigné = persona rattaché à un cas privé (créé par le flux
  // Prep Meeting). Ces personae ne doivent PAS apparaître dans le Sales Coach Réel.
  targetId?: string | null;
  // category = "sales" ou "customer_care" : on ne propose que les personae
  // de la catégorie du simulateur recommandé par le débrief.
  category?: string | null;
  // personaType = "B2B" ou "B2C" : conditionne l'affichage des champs
  // industry/companySize (obligatoires uniquement en B2B Sales).
  personaType?: string | null;
};

// Tronque un texte libre pour l'afficher dans un <option> natif (pas de CSS
// ellipsis fiable sur <option>) — le texte complet reste dans le title au survol.
function truncate(text: string, max = 55): string {
  const clean = text.trim().replace(/\s+/g, " ");
  return clean.length > max ? clean.slice(0, max).trimEnd() + "…" : clean;
}

export function PrepareTrainingFromCoachDialog({ open, context, onClose, onLaunch }: Props) {
  const { t, i18n } = useTranslation();
  const isFr = i18n.language === "fr";
  const [offers, setOffers] = useState<Offer[]>([]);
  const [personae, setPersonae] = useState<Persona[]>([]);
  // 2026-06-12 — Contextes spécifiques au simulateur du débrief (aligné
  // tunnel équipe SimulationCaseWizard). Résolution simulatorKey → simulatorId
  // UUID via la liste des agents pour pouvoir filtrer côté front.
  const [contextsList, setContextsList] = useState<ContextOption[]>([]);
  const [simulatorIdForContexts, setSimulatorIdForContexts] = useState<string | null>(null);
  const [contextId, setContextId] = useState<string | null>(null);
  const [offerId, setOfferId] = useState<string>("");
  const [personaId, setPersonaId] = useState<string>("");
  const [focus, setFocus] = useState<string>("");
  const [difficulty, setDifficulty] = useState<"easy" | "medium" | "hard">("medium");
  // Détails du cas (B2B Sales uniquement — cf. logique Valérie commit d0197ad)
  const [industry, setIndustry] = useState<string[]>([]);
  const [companySize, setCompanySize] = useState<string>("");
  const [customIndustry, setCustomIndustry] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Phase A 2026-05-14 — Quota check au mount du dialog pour éviter que le user
  // remplisse tout le formulaire et se prenne un quota épuisé après avoir cliqué
  // "Lancer". Si épuisé, on remplace le form par un upsell explicite.
  const { quota, loading: quotaLoading } = useQuota();
  const quotaState = classifyQuota(quota);
  const quotaExhausted = quotaState === "exhausted";

  // Charge offres + personae + agents + contextes à l'ouverture.
  // Les agents servent à résoudre simulatorKey → simulatorId UUID nécessaire
  // pour filtrer les contextes spécifiques au simulateur du débrief.
  useEffect(() => {
    if (!open || !context) return;
    setLoading(true);
    setError(null);
    Promise.all([
      accountApi.getOffers().catch(() => []),
      accountApi.getAccountPersonae().catch(() => []),
      authApi.getAgents().catch(() => []),
      accountApi.getContexts({}).catch(() => []),
    ])
      .then(([os, ps, agents, ctxs]: [Offer[], Persona[], any[], any[]]) => {
        setOffers(os ?? []);
        // Catégorie du cas : seul SATISFACTOR (Customer Success) est customer_care,
        // tous les autres simulateurs du Sales Coach Réel sont sales.
        const caseCategory = context?.simulatorKey === "SATISFACTOR" ? "customer_care" : "sales";
        // Résolution simulatorKey (ex "PROSPECTOR") → simulatorId UUID. Mapping
        // aligné sur App.tsx handleStartTrainingFromCoach KEY_TO_SIM_NAME.
        const KEY_TO_SIM_NAME: Record<string, string[]> = {
          PROSPECTOR: ["Prospector"],
          DISCOVERY: ["Discovery Call", "Discovery"],
          PITCH: ["Pitch", "Elevator Pitch"],
          NEGOTIATOR: ["Negotiator"],
          SATISFACTOR: ["Satisfactor", "Customer Success"],
          MEDDIC: ["MEDDIC"],
        };
        const candidates = KEY_TO_SIM_NAME[context!.simulatorKey] ?? [context!.simulatorKey];
        const matchingAgent = (agents ?? []).find((a: any) =>
          candidates.some((c) => a.name?.toLowerCase() === c.toLowerCase()),
        );
        const simId = matchingAgent?.id ?? null;
        setSimulatorIdForContexts(simId);
        setContextsList((ctxs ?? []) as ContextOption[]);
        // Filtres cumulés sur la liste de personae proposée :
        //  - exclut les personae privés rattachés à un cas (targetId) — copies Prep Meeting
        //  - ne garde que les personae de la catégorie du cas (sales vs customer_care)
        const accountPersonae = (ps ?? []).filter(
          (p) => !p.targetId && (p.category || "sales") === caseCategory,
        );
        setPersonae(accountPersonae);
        // Pré-remplit avec le 1er disponible
        if (os && os.length > 0) setOfferId(os[0]!.id);
        if (accountPersonae.length > 0) setPersonaId(accountPersonae[0]!.id);
      })
      .finally(() => setLoading(false));
  }, [open, context]);

  // Pré-remplit le focus + difficulté quand le contexte change
  useEffect(() => {
    if (!context) return;
    const lines: string[] = [];
    if (context.axeTitle) lines.push(`AXE À TRAVAILLER : ${context.axeTitle}`);
    if (context.citation) lines.push(`Ce que j'ai dit : « ${context.citation} »`);
    if (context.suggestedReformulation) lines.push(`Ce que j'aurais pu dire : « ${context.suggestedReformulation} »`);
    if (context.contextNotes) lines.push(`Notes du RDV : ${context.contextNotes}`);
    setFocus(lines.join("\n\n"));
    // Difficulté suggérée à partir du score
    if (typeof context.scoreGlobal === "number") {
      if (context.scoreGlobal < 50) setDifficulty("hard");
      else if (context.scoreGlobal < 70) setDifficulty("medium");
      else setDifficulty("easy");
    }
  }, [context]);

  if (!context) return null;

  const offerEmpty = !loading && offers.length === 0;
  const personaEmpty = !loading && personae.length === 0;

  // Persona sélectionnée + flag B2B Sales (gating affichage industry/companySize).
  // Aligné SimulationCaseWizard (Valérie commit d0197ad) : on n'affiche les
  // 2 champs QUE si la persona est B2B + sales.
  const selectedPersona = personae.find((p) => p.id === personaId);
  const isB2BSales = selectedPersona?.personaType === "B2B" && (selectedPersona.category ?? "sales") === "sales";

  // Validation industry/companySize identique au wizard équipe (gate canNext L196-204
  // de SimulationCaseWizard) : obligatoires en B2B Sales, optionnels ailleurs.
  const hasIndustry =
    industry.some((v) => v !== CUSTOM_INDUSTRY) ||
    (industry.includes(CUSTOM_INDUSTRY) && customIndustry.trim() !== "");
  const b2bDetailsOk = !isB2BSales || (!!companySize && hasIndustry);

  const canLaunch = !!offerId && !!personaId && b2bDetailsOk && !launching;

  async function handleLaunch() {
    if (!canLaunch) return;
    setError(null);
    setLaunching(true);
    try {
      await onLaunch({
        simulatorKey: context!.simulatorKey,
        offerId,
        personaId,
        focus,
        difficulty,
        // Pour B2B Sales uniquement ; sinon arrays/strings vides, ignorés côté backend.
        industry: isB2BSales ? industry : undefined,
        companySize: isB2BSales ? companySize : undefined,
        customIndustry: isB2BSales ? customIndustry : undefined,
        // Contexte spécifique au simulateur ; null si "Aucun" choisi (par défaut).
        contextId,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur au lancement");
      setLaunching(false);
    }
  }

  // Phase A 2026-05-14 — Si quota épuisé, on remplace le formulaire par un
  // message d'upsell. Évite que le user prépare tout pour rien.
  if (quotaExhausted && !quotaLoading) {
    return (
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 bg-amber-100">
                <ShieldAlert className="h-5 w-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <div className="text-base md:text-lg font-bold" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>
                  Quota de sessions atteint
                </div>
                <div className="text-sm font-normal text-gray-500 mt-0.5">
                  Tu ne peux plus lancer de nouvelle simulation ce mois-ci.
                </div>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2 text-sm" style={{ color: "#384872" }}>
            <p>
              Tu as utilisé toutes les sessions incluses dans ton plan
              {quota?.limit != null && ` (${quota.used}/${quota.limit})`}.
              Tu peux acheter un pack de sessions supplémentaires pour reprendre l'entraînement.
            </p>
            {quota?.cycleEnd && (
              <p className="text-xs text-gray-500">
                Ton quota se renouvelle le {new Date(quota.cycleEnd).toLocaleDateString("fr-FR", { day: "2-digit", month: "long" })}.
              </p>
            )}
          </div>
          <div className="flex items-center justify-end gap-2 mt-4">
            <Button variant="outline" onClick={onClose}>Fermer</Button>
            {quota?.ctaUrl && (
              <Button onClick={() => { onClose(); window.location.href = quota.ctaUrl!; }} style={{ background: "#384872", color: "white" }}>
                Acheter un pack
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[calc(100vh-2rem)] sm:max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: "#988EF2" }}>
              <Mic className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-base md:text-lg font-bold" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>
                Préparer ma simulation
              </div>
              {context.axeTitle && (
                <div className="text-sm font-normal text-gray-500 mt-0.5">
                  Pour travailler : <span style={{ color: "#5A4BBE" }}>{context.axeTitle}</span>
                </div>
              )}
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          {/* ① Simulateur (pré-sélectionné, lecture seule en V1) */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500 flex items-center gap-1.5 mb-2">
              <Target className="h-3 w-3" /> Simulateur
            </Label>
            <div className="flex items-center gap-2">
              <Badge className="text-base px-3 py-1.5" style={{ background: "#988EF2", color: "white" }}>
                {context.simulatorLabel}
              </Badge>
              <span className="text-xs text-gray-500">recommandé depuis votre débrief</span>
            </div>
          </div>

          {/* ② Offre */}
          <div>
            <Label htmlFor="offer" className="text-xs uppercase tracking-wider text-gray-500 flex items-center gap-1.5 mb-2">
              <Package className="h-3 w-3" /> Offre concernée
            </Label>
            {offerEmpty ? (
              <div className="text-sm p-3 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-amber-700" />
                <div className="text-amber-900">
                  Vous n'avez pas encore d'offre. Créez-en une depuis <strong>Mon compte → Offres</strong>, puis revenez ici.
                </div>
              </div>
            ) : (
              <select
                id="offer"
                value={offerId}
                onChange={(e) => setOfferId(e.target.value)}
                className="w-full rounded-md border px-3 py-3 text-sm md:text-base bg-white"
                style={{ borderColor: "#D1D5DB", minHeight: 44 }}
                disabled={loading}
              >
                {offers.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            )}
          </div>

          {/* ③ Persona */}
          <div>
            <Label htmlFor="persona" className="text-xs uppercase tracking-wider text-gray-500 flex items-center gap-1.5 mb-2">
              <UserCircle className="h-3 w-3" /> Persona à incarner par l'IA
            </Label>
            {personaEmpty ? (
              <div className="text-sm p-3 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-amber-700" />
                <div className="text-amber-900">
                  Vous n'avez pas encore de persona. Créez-en un depuis <strong>Mon compte → Personae</strong>, puis revenez ici.
                </div>
              </div>
            ) : (
              <select
                id="persona"
                value={personaId}
                onChange={(e) => setPersonaId(e.target.value)}
                className="w-full rounded-md border px-3 py-3 text-sm md:text-base bg-white"
                style={{ borderColor: "#D1D5DB", minHeight: 44 }}
                disabled={loading}
              >
                {personae.map((p) => {
                  const fn = (localizePersonaOption(pickPersonaField(p, "function", i18n.language), i18n.language) || "").trim() || "Persona";
                  // Champ discriminant : painPoints en priorité, sinon profil décideur
                  const hint = (pickPersonaField(p, "painPoints", i18n.language) || pickDecisionMaker(p) || "").trim();
                  const label = hint ? `${fn} — ${truncate(hint)}` : fn;
                  return (
                    <option key={p.id} value={p.id} title={hint ? `${fn} — ${hint}` : fn}>
                      {label}
                    </option>
                  );
                })}
              </select>
            )}
            {context.prospectCompany && (
              <p className="text-xs text-gray-500 mt-1">
                💡 RDV initial : {context.prospectName ?? "?"} · {context.prospectCompany}
              </p>
            )}
          </div>

          {/* ③bis Détails du cas (B2B Sales uniquement, aligné Valérie d0197ad) */}
          {isB2BSales && (
            <div className="space-y-3 pt-2 border-t">
              <Label className="text-xs uppercase tracking-wider text-gray-500 flex items-center gap-1.5">
                <Building2 className="h-3 w-3" /> Détails du cas
              </Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Taille d'entreprise (single-select, obligatoire) */}
                <div className="space-y-1.5">
                  <Label className="text-xs">{t("salesCoach.prepareTraining.companySizeLabel")}</Label>
                  <Select value={companySize} onValueChange={setCompanySize}>
                    <SelectTrigger><SelectValue placeholder="Choisir…" /></SelectTrigger>
                    <SelectContent>
                      {COMPANY_SIZE_OPTIONS.map((opt) => (
                        <SelectItem key={opt} value={opt}>
                          {localizeCompanySize(opt, i18n.language)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {/* Secteur d'activité (multi-select avec "Autre" + champ libre) */}
                <div className="space-y-1.5">
                  <Label className="text-xs">Secteur d'activité</Label>
                  <MultiSelect
                    getLabel={(opt) =>
                      opt === CUSTOM_INDUSTRY
                        ? "Autre"
                        : localizeIndustry(opt, i18n.language)
                    }
                    options={[...INDUSTRY_OPTIONS, CUSTOM_INDUSTRY]}
                    value={industry}
                    onChange={setIndustry}
                    placeholder="Choisir…"
                  />
                  {industry.includes(CUSTOM_INDUSTRY) && (
                    <Input
                      value={customIndustry}
                      onChange={(e) => setCustomIndustry(e.target.value)}
                      placeholder={t("salesCoach.prepareTraining.customIndustryPlaceholder")}
                      className="mt-1.5"
                    />
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ③ter Contexte spécifique au simulateur (2026-06-12).
              Affiché uniquement si au moins un contexte actif existe pour ce
              simulateur. Aligné tunnel équipe SimulationCaseWizard L935-1010 mais
              en format compact (select dropdown) pour rester cohérent avec les
              autres champs du popup débrief. */}
          {(() => {
            const availableContexts = simulatorIdForContexts
              ? contextsList
                  .filter((c) => c.simulatorId === simulatorIdForContexts && c.isActive)
                  .sort((a, b) => (isFr ? a.nameFr : a.nameEn).localeCompare(isFr ? b.nameFr : b.nameEn))
              : [];
            if (availableContexts.length === 0) return null;
            return (
              <div>
                <Label htmlFor="contextId" className="text-xs uppercase tracking-wider text-gray-500 flex items-center gap-1.5 mb-2">
                  <Target className="h-3 w-3" /> Contexte spécifique
                </Label>
                <select
                  id="contextId"
                  value={contextId ?? ""}
                  onChange={(e) => setContextId(e.target.value || null)}
                  className="w-full rounded-md border px-3 py-3 text-sm md:text-base bg-white"
                  style={{ borderColor: "#D1D5DB", minHeight: 44 }}
                  disabled={loading}
                >
                  <option value="">— Aucun contexte —</option>
                  {availableContexts.map((c) => (
                    <option key={c.id} value={c.id} title={(isFr ? c.descriptionFr : c.descriptionEn) || ""}>
                      {isFr ? (c.nameFr || c.nameEn) : (c.nameEn || c.nameFr)}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">{t("salesCoach.prepareTraining.contextOptional")}</p>
              </div>
            );
          })()}

          {/* ④ Focus pour cette simulation */}
          <div>
            <Label htmlFor="focus" className="text-xs uppercase tracking-wider text-gray-500 flex items-center gap-1.5 mb-2">
              <Sparkles className="h-3 w-3" /> Focus de la simulation
            </Label>
            <Textarea
              id="focus"
              value={focus}
              onChange={(e) => setFocus(e.target.value)}
              className="min-h-[120px] text-sm md:text-base font-mono"
              placeholder={t("salesCoach.prepareTraining.focusPlaceholder")}
            />
            <p className="text-xs text-gray-500 mt-1">{t("salesCoach.prepareTraining.focusHint")}</p>
          </div>

          {/* ⑤ Difficulté */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">{t("salesCoach.prepareTraining.difficultyLabel")}</Label>
            <div className="flex gap-2 flex-wrap">
              {(["easy", "medium", "hard"] as const).map((d) => (
                <button
                  key={d}
                  onClick={() => setDifficulty(d)}
                  className={`px-4 h-10 rounded-lg text-sm font-medium border transition-colors ${
                    difficulty === d ? "text-white border-transparent" : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                  style={{ backgroundColor: difficulty === d ? "#5A4BBE" : undefined }}
                >
                  {d === "easy" ? "Facile" : d === "medium" ? "Moyen" : "Difficile"}
                </button>
              ))}
            </div>
            {typeof context.scoreGlobal === "number" && (
              <p className="text-xs text-gray-500 mt-1">
                Suggérée d'après votre score ({context.scoreGlobal}/100).
              </p>
            )}
          </div>

          {error && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /> {error}
            </div>
          )}
        </div>

        {/* CTAs */}
        <div className="flex items-center gap-3 mt-6 sticky bottom-0 bg-white py-2">
          <Button variant="outline" onClick={onClose} disabled={launching} className="h-11">Annuler</Button>
          <Button
            onClick={handleLaunch}
            disabled={!canLaunch || offerEmpty || personaEmpty}
            className="flex-1 h-12 text-base font-semibold"
            style={{ backgroundColor: "#5A4BBE", color: "white" }}
          >
            {launching ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Lancement…</>
            ) : (
              <><Mic className="h-4 w-4 mr-2" /> Lancer ma simulation</>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
